import { formatDate as localeDate } from 'i18n/format';
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { failureOf } from 'components/pipelines/loadStatus';
import RunRef from 'components/pipelines/RunRef';
import { useTokens } from 'theme';
import { clock, plural, rowsLabel } from 'utils/ru';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';

/**
 * The last 14 periods of a load as cells (pipelines.health): a day, a week
 * or a month by its schedule, or one run for a manual load. Hovering or
 * tapping a cell shows its runs.
 */

const FILL = { ok: 'positiveFill', warn: 'warningFill', bad: 'negativeFill', running: 'info' };

const TITLE = {
  get ok() { return t('data.onTime'); },
  get warn() { return t('data.hadFailures'); },
  get bad() { return t('data.noData'); },
  get running() { return t('data.loadingData'); },
  get empty() { return t('data.noRuns'); },
};
const RUN_TITLE = { get ok() { return t('data.succeeded2'); }, get bad() { return t('data.failed'); }, get running() { return t('data.running3'); }, get empty() { return t('data.noRunsYet'); } };

// With more runs than this a period shows its count, its failures and its last run.
const LIST_ALL_UP_TO = 3;

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const dayMonth = (d) => localeDate(d, { day: 'numeric', month: 'long' });

function cellTitle(cell, period) {
  if (period === 'run') return RUN_TITLE[cell.verdict] || RUN_TITLE.empty;
  if (cell.verdict === 'empty' && cell.end && new Date(cell.end) > new Date()) return t('data.noRunsYet');
  return TITLE[cell.verdict] || TITLE.empty;
}

function periodLabel(cell, period) {
  if (period === 'run') {
    const at = cell.runs[0]?.started_at;
    return at ? `${dayMonth(new Date(at))}, ${clock(new Date(at))}` : '';
  }
  const start = new Date(cell.start);
  if (period === 'day') {
    return cap(localeDate(start, { weekday: 'short', day: 'numeric', month: 'long' }));
  }
  if (period === 'week') {
    const end = new Date(new Date(cell.end).getTime() - 1);
    return `${dayMonth(start)} – ${dayMonth(end)}`;
  }
  return cap(localeDate(start, { month: 'long', year: 'numeric' }));
}

function RunLine({ run, period }) {
  const { t } = useTranslation();
  const result = run.status === 'failed'
    ? failureOf(run).text.toLowerCase()
    : run.status === 'success'
      ? (run.row_count != null ? rowsLabel(run.row_count) : t('data.succeeded'))
      : t('data.running');
  return (
    <Box sx={{ fontSize: '0.78rem', lineHeight: 1.5 }}>
      {period === 'run' ? cap(result) : `${clock(new Date(run.started_at))} — ${result}`}
      {run.name && (
        <Box>
          <RunRef name={run.name} />
        </Box>
      )}
    </Box>
  );
}

function CellTip({ cell, period }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const runs = cell.runs || [];
  const shown = runs.length <= LIST_ALL_UP_TO
    ? runs
    : [...runs.filter((r) => r.status === 'failed'), runs[runs.length - 1]].filter(
      (r, i, all) => all.indexOf(r) === i,
    );
  return (
    <Box sx={{ p: 0.5, maxWidth: 280 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{cellTitle(cell, period)}</Typography>
      <Typography sx={{ fontSize: '0.78rem', color: themeTokens.inkMuted, mb: shown.length ? 0.5 : 0 }}>
        {periodLabel(cell, period)}
        {runs.length > LIST_ALL_UP_TO ? ` · ${runs.length} ${plural(runs.length, [t('data.run'), t('data.runs'), t('data.runs2')])}` : ''}
      </Typography>
      {shown.map((run) => (
        <RunLine key={run.public_id} run={run} period={period} />
      ))}
    </Box>
  );
}

export default function HealthStrip({ health, size = 'small' }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const cells = health?.cells || [];
  const big = size === 'large';
  return (
    <Box sx={{ display: 'flex', gap: big ? '4px' : '3px' }} role="list" aria-label={t('data.last14Periods')}>
      {cells.map((cell, i) => {
        const fill = FILL[cell.verdict];
        return (
          <Tooltip
            // Planned cells have a start; manual cells a run; the padding in
            // front of a manual load's first runs has neither and never moves.
            key={cell.start || cell.runs[0]?.public_id || `pad-${i}`}
            title={<CellTip cell={cell} period={health.period} />}
            arrow
            enterTouchDelay={0}
            componentsProps={{
              tooltip: { sx: { bgcolor: themeTokens.surface, color: themeTokens.ink, border: `1px solid ${themeTokens.border}`, boxShadow: 3 } },
              arrow: { sx: { color: themeTokens.surface, '&::before': { border: `1px solid ${themeTokens.border}` } } },
            }}
          >
            <Box
              role="listitem"
              tabIndex={0}
              aria-label={cellTitle(cell, health.period)}
              sx={{
                width: big ? 16 : 8,
                height: big ? 26 : 18,
                borderRadius: big ? '4px' : '3px',
                flexShrink: 0,
                bgcolor: fill ? themeTokens[fill] : 'transparent',
                boxShadow: fill ? 'none' : `inset 0 0 0 1.5px ${themeTokens.borderField}`,
                cursor: 'default',
                '&:hover, &:focus-visible': { outline: `2px solid ${themeTokens.borderStrong}`, outlineOffset: 1 },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}

export function StripLegend() {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const items = [
    ['positiveFill', t('data.onTime2')],
    ['warningFill', t('data.hadFailures2')],
    ['negativeFill', t('data.noData2')],
    [null, t('data.noRuns2')],
  ];
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
      {items.map(([tone, label]) => (
        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.72rem', color: themeTokens.inkMuted }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '2px',
              bgcolor: tone ? themeTokens[tone] : 'transparent',
              boxShadow: tone ? 'none' : `inset 0 0 0 1.5px ${themeTokens.borderField}`,
            }}
          />
          {label}
        </Box>
      ))}
    </Box>
  );
}
